import { Component } from '@theme/component';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

const MAX_AGE = 2 * 60 * 60 * 1000;

function readRecord(key) {
  try {
    const value = JSON.parse(sessionStorage.getItem(key) || 'null');
    return value && Number.isFinite(value.created) && Date.now() - value.created < MAX_AGE ? value : null;
  } catch {
    return null;
  }
}

function writeRecord(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

function resultsURL(value, element) {
  if (!value || value.length > 6000) return null;
  try {
    const url = new URL(value, location.origin);
    const prefix = `${element.dataset.collectionsPath.replace(/\/$/, '')}/`;
    const handle = url.pathname.startsWith(prefix) ? url.pathname.slice(prefix.length) : '';
    const collection = /^[a-z0-9-]+\/?$/.test(handle);
    if (url.origin !== location.origin || (!collection && url.pathname !== element.dataset.searchPath)) return null;
    url.searchParams.delete('bp_return_to');
    return url;
  } catch {
    return null;
  }
}

class BPBrowseContext extends Component {
  #controller;
  #restoreFrame;
  #settleFrame;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    document.addEventListener('click', this.#track, { capture: true, signal });
    document.addEventListener('auxclick', this.#track, { capture: true, signal });
    window.addEventListener('pageshow', this.#restore, { signal });
    if (document.readyState === 'complete') this.#restore();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    cancelAnimationFrame(this.#restoreFrame);
    cancelAnimationFrame(this.#settleFrame);
  }

  #track = (event) => {
    if (!(event.target instanceof Element) || event.defaultPrevented) return;
    if (event.target.closest('button, input, label, select, [data-no-navigation]')) return;
    const card = event.target.closest('results-list product-card');
    if (!card) return;
    const link = event.target.closest('a') || card.querySelector('a.product-card__link');
    if (!(link instanceof HTMLAnchorElement)) return;
    const productURL = new URL(link.href, location.origin);
    if (productURL.origin !== location.origin || !/\/products\/[^/]+\/?$/.test(productURL.pathname)) return;
    const source = new URL(location.href);
    const returning = new URL(source);
    const page = Number(card.closest('[data-page]')?.dataset.page);
    if (Number.isInteger(page) && page > 0 && (page > 1 || returning.searchParams.has('page') || card.closest('results-list')?.getAttribute('infinite-scroll') === 'true')) returning.searchParams.set('page', String(page));
    const anchor = card.querySelector('a.product-card__link[id]');
    if (anchor) returning.hash = anchor.id;
    const safe = resultsURL(returning.href, this);
    if (!safe) return;
    const returnPath = safe.pathname + safe.search + safe.hash;
    const sameTab = event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && (!link.target || link.target === '_self');
    writeRecord(`${this.dataset.contextKey}:${productURL.pathname}`, {
      created: Date.now(), productPath: productURL.pathname, returnPath,
      source: source.href, scrollTop: getScrollTop(), sameTab,
    });
    for (const target of new Set([link, card.querySelector('a.product-card__link')])) {
      if (!(target instanceof HTMLAnchorElement)) continue;
      const destination = new URL(target.href, location.origin);
      if (destination.pathname !== productURL.pathname) continue;
      destination.searchParams.set('bp_return_to', returnPath);
      target.href = destination.pathname + destination.search + destination.hash;
    }
  };

  #restore = () => {
    const key = `${this.dataset.contextKey}:restore`;
    const record = readRecord(key);
    if (!record || record.path !== location.pathname + location.search || !Number.isFinite(record.scrollTop)) return;
    try { sessionStorage.removeItem(key); } catch {}
    cancelAnimationFrame(this.#restoreFrame);
    cancelAnimationFrame(this.#settleFrame);
    this.#restoreFrame = requestAnimationFrame(() => { this.#settleFrame = requestAnimationFrame(() => {
      if (this.isConnected) scrollTo({ top: record.scrollTop, behavior: 'instant' });
    }); });
  };
}

/** @extends {Component<{backLink: HTMLAnchorElement}>} */
class BPProductNavigation extends Component {
  #returning = null;
  #record = null;
  #controller;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    document.addEventListener('click', this.#closeOutside, { capture: true, signal });
    document.addEventListener('focusin', this.#closeOutside, { signal });
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  #closeOutside = (event) => {
    const open = this.querySelector('details[open]');
    if (open && !event.composedPath().includes(open)) open.removeAttribute('open');
  };

  updatedCallback() {
    super.updatedCallback();
    this.#sync();
  }

  #sync() {
    this.#returning = resultsURL(new URL(location.href).searchParams.get('bp_return_to'), this);
    const link = this.refs.backLink;
    if (!this.#returning || !(link instanceof HTMLAnchorElement)) return;
    link.href = this.#returning.pathname + this.#returning.search + this.#returning.hash;
    const record = readRecord(`${this.dataset.contextKey}:${location.pathname}`);
    this.#record = record?.productPath === location.pathname && record.returnPath === link.getAttribute('href') ? record : null;
  }

  /** @param {MouseEvent} event */
  returnToResults(event) {
    if (!this.#returning || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const record = this.#record;
    if (record) writeRecord(`${this.dataset.contextKey}:restore`, {
      created: Date.now(), path: this.#returning.pathname + this.#returning.search, scrollTop: record.scrollTop,
    });
    if (record?.sameTab && history.length > 1 && document.referrer) {
      try {
        const referrer = new URL(document.referrer);
        const source = new URL(record.source);
        referrer.hash = '';
        source.hash = '';
        if (referrer.origin === location.origin && referrer.href === source.href) {
          event.preventDefault();
          history.back();
          return;
        }
      } catch {}
    }
  }

  /** @param {KeyboardEvent} event */
  closeCategories(event) {
    if (event.key !== 'Escape') return;
    const open = this.querySelector('details[open]');
    if (!open) return;
    open.removeAttribute('open');
    open.querySelector('summary')?.focus();
    event.preventDefault();
  }
}

if (!customElements.get('bp-browse-context')) customElements.define('bp-browse-context', BPBrowseContext);
if (!customElements.get('bp-product-navigation')) customElements.define('bp-product-navigation', BPProductNavigation);

